import AchievementsPanel from './ui/AchievementsPanel';
import common from './common';
import Text from './ui/Text';
import Dialog from './ui/Dialog';
import Character from './Character';
import AlertDialog from './ui/AlertDialog';
import EarlyWarningSystem from './ui/EarlyWarningSystem';
import i18next from 'i18next';
import OverviewPanel from './ui/OverviewPanel';
import StatisticPanel from './ui/StatisticPanel';

export default class HUD extends PIXI.Container {
	constructor() {
		super();

		this._achievementsButton = null;
		this._settingsButton = null;
		this._disasterButton = null;
		this._showRiskButton = null;
		this._overviewButton = null;
		this._overview = null;
		this._langSignal = null;

		this._topPanel = null;
		this._budgetPanel = null;
		this._populationPanel = null;
		this._probabilityPanel = null;
		this._panelContainer = null;
		this._isOver = false;
		this._dialog = null;
		this._achievements = null;

		this._fade = null;

		this._popupsLength = 0;

		this._mapPointer = null;
		this._camera = common.world.camera;

		this._popupDebounce = 1;

		this.init();
	}

	init() {
		this._topPanel = this._createTopPanel();
		this.addChild(this._topPanel);

		this._budgetPanel = this._createBudgetPanel();
		this._budgetPanel.interactive = true;

		this._populationPanel = this._createPopulationPanel();
		this._populationPanel.interactive = true;

		this._probabilityPanel = this._createProbabilityPanel();
		this._probabilityPanel.interactive = true;

		this._panelContainer = new PIXI.Container();

		this._budgetPanel.position.set(0, 0);
		this._populationPanel.position.set(this._budgetPanel.width + 5, 0);
		this._probabilityPanel.position.set(this._populationPanel.x + this._populationPanel.width + 5, 0);
		this._panelContainer.position.set(common.STAGE_WIDTH * 0.5 - this._panelContainer.width * 0.5, common.STAGE_HEIGHT - this._budgetPanel.height * this._panelContainer.scale.y);
		this._panelContainer.interactive = true;
		this._panelContainer.scale.set(PIXI.utils.isMobile.apple.tablet ? 0.9 : 1);
		this._panelContainer.addChild(this._budgetPanel, this._populationPanel, this._probabilityPanel);
		this.addChild(this._panelContainer);

		this._achievements = new AchievementsPanel();
		this._achievements.visible = false;
		this._achievements.position.set(common.STAGE_WIDTH * 0.5, common.STAGE_HEIGHT * 0.5);
		this.addChild(this._achievements);

		this._achievements.signals.unlocked.add(() => {
			this._achievementsButton.interactive = true;
			this._achievementsButton.buttonMode = true;
		});

		this._overview = new OverviewPanel();
		this._overview.visible = false;
		this._overview.position.set(common.STAGE_WIDTH * 0.5, common.STAGE_HEIGHT * 0.5);
		this.addChild(this._overview);

		this._picture = null;

		this._ews = new EarlyWarningSystem();
		this._ews.position.set(common.STAGE_WIDTH * 0.5, -28);
		this.addChild(this._ews);

		this._createButtons();

		this.probability = 0;
		this._createEvents();

		const general = Object.keys(P3.assets.json(`language_${common.config.language}`)).filter((key) => key.startsWith('gui.game.popups.popup'));
		const scenario = Object.keys(P3.assets.json(`language_${common.config.language}`)).filter((key) => key.startsWith(`${common.selectedLevel}.popup.popup`));

		this._popupsLength = general.length + scenario.length;

		this._fade = new PIXI.Graphics();
		this._fade.beginFill(0x0, 0.4);
		this._fade.drawRect(0, 0, P3.View.width, P3.View.height);
		this._fade.endFill();
		this._fade.position.set((common.STAGE_WIDTH - P3.View.width) * 0.5, 0);
		this._fade.interactive = true;
		this._fade.visible = false;
		this.addChild(this._fade);

		this._alert = new AlertDialog('', '', 'sad');
		this._alert.position.set(common.STAGE_WIDTH * 0.5, common.STAGE_HEIGHT * 0.5);
		this._alert.visible = false;
		this.addChild(this._alert);

		this._langSignal = common.languageManager.signals.changed.add(() => {
			P3.utils.performNextTick(() => {
				this.resize();
			});
		});
	}

	_createEvents() {
		for (const object of this.children) {
			object.interactive = true;

			object.on('pointerover', () => {
				this._isOver = true;
			}, this);

			object.on('pointerout', () => {
				this._isOver = false;
			}, this);

			if (object.type) {
				object.on('pointertap', () => {
					this._panelInfoDialog(object, object.type);
					P3.audio.playSound('sfx_popup');
				}, this);
			}
		}
		for (const object of this._panelContainer.children) {
			object.interactive = true;

			object.on('pointerover', () => {
				this._isOver = true;
			}, this);

			object.on('pointerout', () => {
				this._isOver = false;
			}, this);

			if (object.type) {
				object.on('pointertap', () => {
					this._panelInfoDialog(object, object.type);
					P3.audio.playSound('sfx_popup');
				}, this);
			}
		}
	}

	_panelInfoDialog(panel, type) {
		const childIndex = panel.parent.getChildIndex(panel);

		this.toggleFade(true);
		this.closeOpenDialog();

		panel.parent.addChild(panel);
		common.paused = true;

		this._dialog = new Dialog(550, -1, null);
		this._dialog.setBodyText(`gui.game.information.panels.${type}`);
		this._dialog.setTitleText('gui.game.information.title');
		this.addChild(this._dialog);

		const okButton = common.buttonFactory.createButton({
			normal: { textureName: 'btn_blue_fill_large_9slice_out', width: 220, height: 75, left: 35, top: 35, right: 35, bottom: 35 },
			over: { textureName: 'btn_blue_fill_large_9slice_over', width: 220, height: 75, left: 35, top: 35, right: 35, bottom: 35 },
		});

		okButton.position.set(0, this._dialog.body.text.y + this._dialog.body.text.height + 60);
		okButton.signals.click.add(() => {
			common.popupOpen = false;
			common.paused = false;
			this._dialog.destroy();
			this.toggleFade(false);
			panel.parent.addChildAt(panel, childIndex);
		}, this);
		okButton.overSoundName = 'sfx_genericrollover';
		this._dialog.addChild(okButton);

		okButton.label = new Text('gui.general.options.ok', { font: '30px', fontWeight: 'bold', align: 'center', tint: 0xFFFFFF });
		okButton.label.anchor.set(0.5);
		okButton.animation.addChild(okButton.label);

		okButton.accessibleTitle = okButton.label.text;

		this._dialog.resize(okButton.y + okButton.height + 20);
		this._dialog.position.set(common.STAGE_WIDTH * 0.5, common.STAGE_HEIGHT * 0.5 - this._dialog.height * 0.5);
		this._dialog.scale.set(0.8);

		this._dialog.closeButton.on('pointertap', () => {
			this.toggleFade(false);
			this._dialog.destroy();
			this._dialog = null;
			common.popupOpen = false;
			common.paused = false;
			panel.parent.addChildAt(panel, childIndex);
		}, this);

		new TWEEN.Tween(this._dialog.scale, this.tweens)
			.to({
				x: 1,
				y: 1,
			}, 500)
			.easing(TWEEN.Easing.Back.Out)
			.start();
		common.popupOpen = true;
	}

	toggleFade(state) {
		const alertIndex = this.getChildIndex(this._alert);

		this._fade.visible = state;
		this.addChildAt(this._fade, alertIndex - 1);
	}

	_createButtons() {
		this._achievementsButton = this._createAdvisor();
		this._achievementsButton.interactive = false;
		this._achievementsButton.buttonMode = false;
		this._achievementsButton.scale.set(0.78);
		this._achievementsButton.position.set((common.STAGE_WIDTH - P3.View.width + this._achievementsButton.width) * 0.5, 120);
		this.addChild(this._achievementsButton);

		let states = new P3.ButtonStates();

		states.normal = P3.assets.texture('btn_black_fill_small_out');
		states.over = P3.assets.texture('btn_black_fill_small_over');
		states.icon = P3.assets.texture('icon_settings');

		this._settingsButton = common.buttonFactory.createButton(states);
		this.addChild(this._settingsButton);
		this._settingsButton.accessibleTitle = i18next.t('accessibility.button.settings');

		const disasterLabel = new Text('gui.game.buttons.disaster', { font: '30px', fontWeight: 'bold', align: 'center', tint: 0xFFFFFF, maxWidth: 210, lineHeight: 0 });
		const showRiskLabel = new Text('gui.game.buttons.risk', { font: '30px', fontWeight: 'bold', align: 'left', tint: 0x0, maxWidth: 140, lineHeight: 0 });
		const buttonHeight = Math.max(75, Math.max(disasterLabel.height, showRiskLabel.height) + 20);

		this._disasterButton = common.buttonFactory.createButton({
			normal: { textureName: 'btn_start_disaster_9slice_out', width: 285, height: buttonHeight, left: 20, top: 20, right: 20, bottom: 20 },
			over: { textureName: 'btn_start_disaster_9slice_out_over', width: 285, height: buttonHeight, left: 20, top: 20, right: 20, bottom: 20 },
		});

		this._disasterButton._icon = new PIXI.Container();
		this._disasterButton.addChild(this._disasterButton._icon);
		this._disasterButton.position.set(this._panelContainer.x + this._panelContainer.width - this._disasterButton.width * 0.5 - 5, this._panelContainer.y - buttonHeight * 0.5 + 50);
		this.addChild(this._disasterButton);

		const filter = new PIXI.filters.ColorMatrixFilter();

		filter.desaturate();
		this._disasterButton.filters = [filter];
		this._disasterButton.animate = false;
		this._disasterButton.animateIcon = false;

		this._disasterButton.icon = new PIXI.Sprite(P3.assets.texture(`icon_${common.selectedLevel}`));
		this._disasterButton.icon.anchor.set(0, 0.5);
		this._disasterButton.icon.scale.set(0.25);
		this._disasterButton.icon.position.set(this._disasterButton.width * -0.5 + 20, -6);
		this._disasterButton.animation.addChild(this._disasterButton.icon);

		this._disasterButton.label = disasterLabel;
		this._disasterButton.label.anchor.set(0.5);
		this._disasterButton.label.position.set(20, -6);
		this._disasterButton.animation.addChild(this._disasterButton.label);

		this._disasterButton.accessibleTitle = this._disasterButton.label.text;

		this._showRiskButton = common.buttonFactory.createButton({
			normal: { textureName: 'btn_risk_9slice_out', width: 220, height: buttonHeight, left: 20, top: 20, right: 20, bottom: 20 },
			over: { textureName: 'btn_risk_9slice_out_over', width: 220, height: buttonHeight, left: 20, top: 20, right: 20, bottom: 20 },
		});

		this._showRiskButton.position.set(this._disasterButton.x - this._showRiskButton.width - 30, this._disasterButton.y);
		this.addChild(this._showRiskButton);

		this._showRiskButton.icon = new PIXI.Sprite(P3.assets.texture('icon_warning'));
		this._showRiskButton.icon.anchor.set(0, 0.5);
		this._showRiskButton.icon.position.set(this._showRiskButton.width * -0.5 + 20, -6);
		this._showRiskButton.animation.addChild(this._showRiskButton.icon);

		this._showRiskButton.label = showRiskLabel;
		this._showRiskButton.label.anchor.set(0.5);
		this._showRiskButton.label.position.set(20, -6);
		this._showRiskButton.animation.addChild(this._showRiskButton.label);

		this._showRiskButton.accessibleTitle = this._showRiskButton.label.text;

		this._completeButton = new PIXI.NineSlicePlane(P3.assets.texture('btn_blue_fill_large_9slice_out'), 35, 35, 35, 35);
		this._completeButton.width = 285;
		this._completeButton.height = 75;
		this._completeButton.visible = false;
		this._completeButton.position.set(this._disasterButton.x - this._completeButton.width * 0.5, this._disasterButton.y - this._disasterButton.height * 0.5 - 5);
		this._completeButton.interactive = true;
		this._completeButton.buttonMode = true;
		this.addChild(this._completeButton);

		this._completeButton.label = new Text('gui.game.buttons.finished', { font: '30px', fontWeight: 'bold', align: 'left', tint: 0xFFFFFF });
		this._completeButton.label.anchor.set(0, 0.5);
		this._completeButton.label.position.set(40, this._completeButton.height * 0.5);
		this._completeButton.addChild(this._completeButton.label);

		this._completeButton.icon = new PIXI.Sprite(P3.assets.texture('icon_arrow_r'));
		this._completeButton.icon.anchor.set(0.5);
		this._completeButton.icon.position.set(this._completeButton.width - this._completeButton.icon.width, this._completeButton.height * 0.5);
		this._completeButton.addChild(this._completeButton.icon);

		this._completeButton.accessibleTitle = this._completeButton.label.text;

		states = new P3.ButtonStates();
		states.normal = P3.assets.texture('btn_game_overview_out');
		states.over = P3.assets.texture('btn_game_overview_over');

		this._overviewButton = common.buttonFactory.createButton(states);
		this.addChild(this._overviewButton);

		this._overviewButton.accessibleTitle = i18next.t('accessibility.button.overview');
	}

	_createAdvisor() {
		const avatar = new PIXI.Container();

		avatar.bg = new PIXI.Sprite(P3.assets.texture('character_bg'));
		avatar.bg.anchor.set(0.5);
		avatar.addChild(avatar.bg);

		avatar.fg = new PIXI.Sprite(P3.assets.texture('character_fg'));
		avatar.fg.anchor.set(0.5);
		avatar.addChild(avatar.fg);

		this._picture = new Character();
		this._picture.view.scale.set(0.5);
		this._picture.position.set(0, avatar.bg.height * 0.5);
		avatar.addChild(this._picture);
		this._picture.playAnimation('happy');
		this._picture.update();

		avatar.circleMask = new PIXI.Graphics();
		avatar.circleMask.position.set(avatar.bg.width * 0.5, avatar.bg.height * 0.5);
		avatar.circleMask.beginFill(0x0);
		avatar.circleMask.drawCircle(-(avatar.bg.width * 0.5), -(avatar.bg.height * 0.5), 74);
		avatar.circleMask.endFill();
		avatar.addChild(avatar.circleMask);
		this._picture.mask = avatar.circleMask;

		avatar.notificationIcon = new PIXI.Sprite(P3.assets.texture('btn_key_fact_new_calltoaction_out'));
		avatar.notificationIcon.anchor.set(0.5);
		avatar.notificationIcon.position.set(avatar.bg.width * 0.5 - 20, avatar.bg.height * 0.5 - 20);
		avatar.addChild(avatar.notificationIcon);

		avatar.notificationIcon.label = new Text('1', { font: '38px', fontWeight: 'bold', align: 'left', tint: 0xffffff });
		avatar.notificationIcon.label.anchor.set(0.5);
		avatar.notificationIcon.addChild(avatar.notificationIcon.label);

		avatar.notificationIcon.visible = false;

		avatar.scale.set(1.2);

		return avatar;
	}

	_createTopPanel() {
		const topPanel = new PIXI.Container();

		topPanel.type = 'toppanel';

		topPanel.background = new PIXI.Graphics();
		topPanel.background.beginFill(0x0, 1);
		topPanel.background.drawRect(0, 0, P3.View.width, 35);
		topPanel.background.endFill();
		topPanel.addChild(topPanel.background);

		const text
            = `${i18next.t('gui.general.disasters.disaster')}: ${i18next.t(`gui.general.disasters.${common.selectedLevel}`)} (${i18next.t(`gui.general.difficulties.${common.scenario.difficulty}`)})`;

		topPanel.disasterLabel = new Text(text, { font: '32px', fontWeight: 'bold', align: 'left', tint: 0xFFFFFF });
		topPanel.disasterLabel.signals.languageChange.add(() => {
			topPanel.disasterLabel.text = `${i18next.t('gui.general.disasters.disaster')}: ${i18next.t(`gui.general.disasters.${common.selectedLevel}`)} (${i18next.t(`gui.general.difficulties.${common.scenario.difficulty}`)})`;
		});
		topPanel.disasterLabel.anchor.set(0, 0.5);
		topPanel.disasterLabel.position.set(30, topPanel.background.height * 0.5);
		topPanel.addChild(topPanel.disasterLabel);

		topPanel.scoreValue = new Text('0', { font: '32px', fontWeight: 'bold', align: 'left', tint: 0xFFFFFF });
		topPanel.scoreValue.anchor.set(1, 0.5);
		topPanel.scoreValue.position.set(topPanel.background.width, topPanel.background.height * 0.5);
		topPanel.addChild(topPanel.scoreValue);

		topPanel.scoreLabel = new Text(`${i18next.t('gui.toppanel.score')}: `, { font: '32px', fontWeight: 'bold', align: 'left', tint: 0xFFFFFF });
		topPanel.scoreLabel.signals.languageChange.add(() => {
			topPanel.scoreLabel.text = `${i18next.t('gui.toppanel.score')}: `;
		});
		topPanel.scoreLabel.anchor.set(1, 0.5);
		topPanel.scoreLabel.position.set(topPanel.scoreValue.x - topPanel.scoreValue.width - 60, topPanel.background.height * 0.5);
		topPanel.addChild(topPanel.scoreLabel);

		return topPanel;
	}

	_createBudgetPanel() {
		const panel = new StatisticPanel('budget');

		panel.addItem('budget', 'gui.budgetpanel.budget', `$${common.numberWithCommas(common.scenario.budget.total)}`);
		panel.addItem('remaining', 'gui.budgetpanel.remaining', `$${common.numberWithCommas(common.scenario.budget.remaining)}`);
		panel.addItem('housing', 'gui.budgetpanel.housing', `$${common.numberWithCommas(common.scenario.budget.housing)}`);
		panel.addItem('defences', 'gui.budgetpanel.defences', `$${common.numberWithCommas(common.scenario.budget.defences)}`);

		return panel;
	}

	_createPopulationPanel() {
		const panel = new StatisticPanel('population');

		panel.addItem('population', 'gui.populationpanel.population', common.scenario.population.total.toString(), 'icon_person');
		panel.addItem('unhoused', 'gui.populationpanel.unhoused', common.scenario.population.unhoused.toString(), 'icon_person');
		panel.addItem('housed', 'gui.populationpanel.housed', common.scenario.population.housed.toString(), 'icon_person');
		panel.addItem('sheltered', 'gui.populationpanel.sheltered', common.scenario.population.sheltered.toString(), 'icon_person');

		return panel;
	}

	_createProbabilityPanel() {
		const panel = new PIXI.Container();

		panel.type = 'probability';

		panel.background = new PIXI.NineSlicePlane(P3.assets.texture('panel_probability_9slice'), 20, 50, 20, 20);
		panel.background.width = 512;
		panel.background.height = 152;
		panel.background.position.set(0, 0);
		panel.addChild(panel.background);

		panel.map = new PIXI.Sprite(P3.assets.texture(`map_${common.selectedLevel}`));
		panel.map.width = 130;
		panel.map.height = 80;
		panel.addChild(panel.map);
		panel.map.position.set(10, 54);

		this._mapPointer = new PIXI.Graphics();
		this._mapPointer.lineStyle(2, 0x0);
		this._mapPointer.drawRect(0, 0, 34, 26);
		panel.addChild(this._mapPointer);

		panel.map.pointerMask = new PIXI.Graphics();
		panel.map.pointerMask.beginFill(0x0);
		panel.map.pointerMask.drawRect(0, 0, 146, 90);
		panel.map.pointerMask.endFill();
		panel.map.pointerMask.position.set(10, 54);
		panel.addChild(panel.map.pointerMask);
		this._mapPointer.mask = panel.map.pointerMask;

		panel.probabilitylabel = new Text('gui.probabilitypanel.probability', { font: '30px', fontWeight: 'bold', align: 'left', tint: 0x0 });
		panel.probabilitylabel.position.set(panel.map.x + panel.map.width + 10, panel.map.y);
		panel.addChild(panel.probabilitylabel);

		panel.probabilityBar = new PIXI.Container();
		panel.addChild(panel.probabilityBar);

		panel.probabilityBar.back = new PIXI.Sprite(P3.assets.texture('probability_bar_off'));
		panel.probabilityBar.back.position.set(panel.map.x + panel.map.width + 5, 90);
		panel.probabilityBar.addChild(panel.probabilityBar.back);

		panel.probabilityBar.front = new PIXI.Sprite(P3.assets.texture('probability_bar_on'));
		panel.probabilityBar.front.position.set(panel.map.x + panel.map.width + 5, 90);
		panel.probabilityBar.addChild(panel.probabilityBar.front);

		panel.probabilityBar.barMask = new PIXI.Graphics();
		panel.probabilityBar.barMask.beginFill(0x0);
		panel.probabilityBar.barMask.drawRect(8, 0, panel.probabilityBar.front.width - 16, panel.probabilityBar.height);
		panel.probabilityBar.barMask.endFill();
		panel.probabilityBar.addChild(panel.probabilityBar.barMask);
		panel.probabilityBar.barMask.x = panel.probabilityBar.front.x;
		panel.probabilityBar.barMask.y = panel.probabilityBar.front.y;

		panel.probabilityBar.front.mask = panel.probabilityBar.barMask;

		panel.probabilityBar.pinter = new PIXI.Sprite(P3.assets.texture('probability_pointer'));
		panel.probabilityBar.pinter.anchor.set(0.5, 1);
		panel.probabilityBar.pinter.x = panel.probabilityBar.barMask.x + panel.probabilityBar.barMask.width + 8;
		panel.probabilityBar.pinter.y = panel.probabilityBar.barMask.y + panel.probabilityBar.barMask.height;
		panel.probabilityBar.addChild(panel.probabilityBar.pinter);

		panel.probabilityBar.value0 = new Text('0', { font: '30px', align: 'left', tint: 0x0 });
		panel.probabilityBar.value0.anchor.set(0, 0);
		panel.probabilityBar.value0.position.set(panel.probabilityBar.front.x, panel.probabilityBar.front.y + 20);
		panel.probabilityBar.addChild(panel.probabilityBar.value0);

		panel.probabilityBar.value25 = new Text('25', { font: '30px', align: 'left', tint: 0x0 });
		panel.probabilityBar.value25.anchor.set(0.5, 0);
		panel.probabilityBar.value25.position.set(panel.probabilityBar.front.x + panel.probabilityBar.front.width * 0.25, panel.probabilityBar.front.y + 20);
		panel.probabilityBar.addChild(panel.probabilityBar.value25);

		panel.probabilityBar.value50 = new Text('50', { font: '30px', align: 'left', tint: 0x0 });
		panel.probabilityBar.value50.anchor.set(0.5, 0);
		panel.probabilityBar.value50.position.set(panel.probabilityBar.front.x + panel.probabilityBar.front.width * 0.5, panel.probabilityBar.front.y + 20);
		panel.probabilityBar.addChild(panel.probabilityBar.value50);

		panel.probabilityBar.value75 = new Text('75', { font: '30px', align: 'left', tint: 0x0 });
		panel.probabilityBar.value75.anchor.set(0.5, 0);
		panel.probabilityBar.value75.position.set(panel.probabilityBar.front.x + panel.probabilityBar.front.width * 0.75, panel.probabilityBar.front.y + 20);
		panel.probabilityBar.addChild(panel.probabilityBar.value75);

		panel.probabilityBar.value100 = new Text('100', { font: '30px', align: 'left', tint: 0x0 });
		panel.probabilityBar.value100.anchor.set(1, 0);
		panel.probabilityBar.value100.position.set(panel.probabilityBar.front.x + panel.probabilityBar.front.width, panel.probabilityBar.front.y + 20);
		panel.probabilityBar.addChild(panel.probabilityBar.value100);

		return panel;
	}

	destroy() {
		// clean up goes here
		common.languageManager.signals.changed.detach(this._langSignal);
		this._budgetPanel.destroy();
		this._populationPanel.destroy();
		this._ews.destroy();

		super.destroy();
	}

	resize() {
		this._achievements.resize();
		this._overview.resize();

		this._alert.position.set(common.STAGE_WIDTH * 0.5, common.STAGE_HEIGHT * 0.5 - this._alert.height * 0.5);
		this._achievements.position.set(common.STAGE_WIDTH * 0.5, common.STAGE_HEIGHT * 0.5);
		this._overview.position.set(common.STAGE_WIDTH * 0.5, common.STAGE_HEIGHT * 0.5);
		this._ews.position.set(common.STAGE_WIDTH * 0.5, this._ews.extended ? 119 : -28);

		this._budgetPanel.position.set(0, 0);
		this._populationPanel.position.set(this._budgetPanel.width + 5, 0);
		this._probabilityPanel.position.set(this._populationPanel.x + this._populationPanel.width + 5, 0);
		this._panelContainer.position.set(common.STAGE_WIDTH * 0.5 - this._panelContainer.width * 0.5, common.STAGE_HEIGHT - this._budgetPanel.height * this._panelContainer.scale.y + 3);

		this._disasterButton.position.set(this._panelContainer.x + this._panelContainer.width - this._disasterButton.width * 0.5 - 5, this._panelContainer.y - this._disasterButton.height * 0.5 + 50);
		this._showRiskButton.position.set(this._disasterButton.x - this._showRiskButton.width - 30, this._disasterButton.y);
		this._completeButton.position.set(this._disasterButton.x - this._completeButton.width * 0.5, this._disasterButton.y - this._disasterButton.height * 0.5 - 5);

		this._settingsButton.position.set((common.STAGE_WIDTH + P3.View.width) * 0.5 - 70, 105);
		this._overviewButton.position.set((common.STAGE_WIDTH - P3.View.width) * 0.5 + 50, 250);

		if (this._achievementsButton) {
			this._achievementsButton.x = (common.STAGE_WIDTH - P3.View.width + this._achievementsButton.width + 40) * 0.5;
		}

		if (this._dialog) {
			this._dialog.position.set(common.STAGE_WIDTH * 0.5, common.STAGE_HEIGHT * 0.5 - this._dialog.height * 0.5);
		}

		if (this._fade) {
			this._fade.width = P3.View.width;
			this._fade.height = P3.View.height;
			this._fade.position.set((common.STAGE_WIDTH - P3.View.width) * 0.5, 0);
		}

		this._topPanel.position.set((common.STAGE_WIDTH - P3.View.width) * 0.5, 0);
		this._topPanel.background.width = P3.View.width;
		this._topPanel.scoreValue.x = P3.View.width - 30;
		this._topPanel.scoreLabel.x = this._topPanel.scoreValue.x - this._topPanel.scoreValue.width - 10;
	}

	update() {
		this._picture.update();

		if (this._achievements.visible) {
			this._achievements.update();
		}

		if (this.overview.visible) {
			this.overview.update();
		}

		if (this._alert.visible) {
			this._alert.update();
		}

		if (!common.paused && this._ews._extended) {
			this._ews.update();
		}

		this._mapPointer.position.set((this._camera.target.x * 0.033) - this._mapPointer.width / 2, (this._camera.target.y * 0.07) - this._mapPointer.height / 2);

		if (common.debug && P3.input.isKeyDown(P3.keys.SPACE) && this._alert.visible === false) {
			this.showAlertDialog();
		}

		if (common.scenario.elapsedTime === 0 || common.scenario.disaster.isRunning || this.ews._extended) {
			return;
		}

		this._popupDebounce -= window.dt;

		if ((common.scenario.elapsedTime % (common.scenario.time / this._popupsLength) === 0) && (this._popupDebounce <= 0) && this._alert.visible === false) {
			this.showAlertDialog();
		}
	}

	showTileInfoDialog(tile) {
		this.toggleFade(true);
		this.closeOpenDialog();

		this._dialog = new Dialog(500, -1, { _func: this.onDialogDestroy, _scope: this });
		this.addChild(this._dialog);
		common.paused = true;

		let data = tile.structure > 1 ? tile.data.structure : tile.data.ground;

		if (!data) {
			data = { description: '', info: '' };
		}
		this._dialog.setTitleText(data.description);
		this._dialog.setBodyText(data.info);

		this._dialog.panel.titleLabel.position.set(100, 45);

		this._dialog.panel.body.riskTooltip = new PIXI.Sprite(P3.assets.texture(`tooltip_tile_information_corner_${tile.risk + 1}`));
		this._dialog.panel.body.riskTooltip.anchor.set(0.5);
		this._dialog.panel.body.riskTooltip.position.set(55, 60);
		this._dialog.panel.body.addChild(this._dialog.panel.body.riskTooltip);

		this._dialog.panel.body.toolTipLabel = new Text((tile.risk + 1).toString(), { font: '30px', fontWeight: 'bold', align: 'left', tint: 0xffffff });
		this._dialog.panel.body.toolTipLabel.anchor.set(0.5);
		this._dialog.panel.body.toolTipLabel.position.set(this._dialog.panel.body.riskTooltip.x, this._dialog.panel.body.riskTooltip.y);
		this._dialog.panel.body.addChild(this._dialog.panel.body.toolTipLabel);

		this._dialog.panel.body.risk = new PIXI.Sprite(P3.assets.texture('icon_warning'));
		this._dialog.panel.body.risk.anchor.set(0.5);
		this._dialog.panel.body.risk.scale.set(0.8);
		this._dialog.panel.body.risk.position.set(55, this._dialog.body.text.y + this._dialog.body.text.height + 25);
		this._dialog.panel.body.addChild(this._dialog.panel.body.risk);

		this._dialog.panel.body.risk.label = new Text(`${100 - tile.strength}%`, { font: '30px', fontWeight: 'bold', align: 'left', tint: 0x0 });
		this._dialog.panel.body.risk.label.anchor.set(0, 0.5);
		this._dialog.panel.body.risk.label.position.set(this._dialog.panel.body.risk.x + 20, this._dialog.panel.body.risk.y);
		this._dialog.panel.body.addChild(this._dialog.panel.body.risk.label);

		this._dialog.panel.body.population = new PIXI.Sprite(P3.assets.texture('icon_contextual_develop'));
		this._dialog.panel.body.population.anchor.set(0.5);
		this._dialog.panel.body.population.scale.set(0.6);
		this._dialog.panel.body.population.position.set(160, this._dialog.body.text.y + this._dialog.body.text.height + 23);
		this._dialog.panel.body.addChild(this._dialog.panel.body.population);

		const t = tile.structure > 1 ? tile.data.structure : tile.data.ground;
		const pop = t && t.population ? t.population : 0;

		this._dialog.panel.body.population.label = new Text(pop.toString(), { font: '30px', fontWeight: 'bold', align: 'left', tint: 0x0 });
		this._dialog.panel.body.population.label.anchor.set(0, 0.5);
		this._dialog.panel.body.population.label.position.set(this._dialog.panel.body.population.x + 20, this._dialog.panel.body.population.y);
		this._dialog.panel.body.addChild(this._dialog.panel.body.population.label);

		this._dialog.resize(this._dialog.panel.body.risk.y + this._dialog.panel.body.risk.height + 40);

		this._dialog.position.set(common.STAGE_WIDTH * 0.5, common.STAGE_HEIGHT * 0.5 - this._dialog.height * 0.5);

		P3.audio.playSound('sfx_popup');

		this._dialog.scale.set(0.8);
		new TWEEN.Tween(this._dialog.scale, this.tweens)
			.to({
				x: 1,
				y: 1,
			}, 300)
			.easing(TWEEN.Easing.Back.Out)
			.start();

		common.popupOpen = true;
	}

	showAlertDialog() {
		common.popupOpen = true;
		common.paused = true;

		this._fade.visible = true;
		this._alert.visible = true;
		this._alert.setBodyText(this.randomPopup());
		this._alert.setTitleText('gui.general.advisor');
		this._alert.character.playAnimation('sad');
		this._alert.resize();
		new TWEEN.Tween(this._alert.scale, this._tweens)
			.to({
				x: 1,
				y: 1,
			}, 300)
			.easing(TWEEN.Easing.Back.Out)
			.start();
		P3.audio.playSound('sfx_advisorpopup');

		this._alert.signals.close.once(() => {
			this._popupDebounce = 1;
			common.popupOpen = false;
			common.paused = false;
			this._fade.visible = false;
		});
	}

	closeOpenDialog() {
		if (this._dialog) {
			this._dialog.destroy();
		}

		if (this._achievements) {
			this._achievements.visible = false;
		}

		if (this._alert.visible) {
			this._alert.onCloseButtonClick();
		}

		this._dialog = null;
		common.popupOpen = false;
	}

	onDialogDestroy() {
		common.paused = false;
		this._dialog = null;

		this.toggleFade(false);
	}

	randomPopup() {
		const general = Object.keys(P3.assets.json(`language_${common.config.language}`)).filter((key) => key.startsWith('gui.game.popups.popup'));
		const scenario = Object.keys(P3.assets.json(`language_${common.config.language}`)).filter((key) => key.startsWith(`${common.selectedLevel}.popup.popup`));

		this._popupsLength = general.length + scenario.length;

		if (Math.random() > 0.5) {
			return `${general[P3.math.randomInt(0, general.length - 1)]}`;
		}

		return `${scenario[P3.math.randomInt(0, scenario.length - 1)]}`;
	}

	get settingsButton() {
		return this._settingsButton;
	}

	get achievementsButton() {
		return this._achievementsButton;
	}

	get overviewButton() {
		return this._overviewButton;
	}

	get disasterButton() {
		return this._disasterButton;
	}

	get overview() {
		return this._overview;
	}

	get achievements() {
		return this._achievements;
	}

	get showRiskButton() {
		return this._showRiskButton;
	}

	get completeButton() {
		return this._completeButton;
	}

	get probabilityPanel() {
		return this._probabilityPanel;
	}

	get budgetPanel() {
		return this._budgetPanel;
	}

	get populationPanel() {
		return this._populationPanel;
	}

	get isOver() {
		return this._isOver;
	}

	set disasterText(text) {
		this._topPanel.disasterLabel.text = i18next.t(text);
	}

	get disasterText() {
		return this._topPanel.disasterLabel.text;
	}

	set score(value) {
		this._topPanel.scoreValue.text = value;
	}

	get score() {
		return this._topPanel.scoreValue.text;
	}

	get picture() {
		return this._picture;
	}

	get ews() {
		return this._ews;
	}

	get dialog() {
		return this._dialog;
	}

	set dialog(v) {
		this._dialog = v;
	}

	/**
     * Sets the probabilty pinter value. value must be percentage in range 0-100
     * @param value {Number}
     */
	set probability(value) {
		value = 100 - value;

		const { barMask, pinter, front } = this._probabilityPanel.probabilityBar;

		barMask.x = front.x - barMask.width * (value / 100);
		pinter.x = barMask.x + barMask.width + 8;
	}

	get probability() {
		return this._probability;
	}

	/**
     * Sets the values for the budget panel. Numbers are automatically formated with commas for every thounsandth unit.
     */
	updateBudget() {
		this._budgetPanel.getItem('remaining').value.text = `$${common.numberWithCommas(common.scenario.budget.remaining)}`;
		this._budgetPanel.getItem('housing').value.text = `$${common.numberWithCommas(common.scenario.budget.housing)}`;
		this._budgetPanel.getItem('defences').value.text = `$${common.numberWithCommas(common.scenario.budget.defences)}`;
	}

	/**
     * Sets the values for the population panel. Numbers are automatically formated with commas for every thousandth unit.
     */
	updatePopulation() {
		this._populationPanel.getItem('population').value.text = common.numberWithCommas(common.scenario.population.total);
		this._populationPanel.getItem('unhoused').value.text = common.numberWithCommas(common.scenario.population.unhoused);
		this._populationPanel.getItem('housed').value.text = common.numberWithCommas(common.scenario.population.housed);
		this._populationPanel.getItem('sheltered').value.text = common.numberWithCommas(common.scenario.population.sheltered);
	}

	updateScore() {
		this._topPanel.scoreValue.text = common.score;

		this._topPanel.scoreValue.x = P3.View.width - 30;
		this._topPanel.scoreLabel.x = this._topPanel.scoreValue.x - this._topPanel.scoreValue.width - 10;
	}
}
